import {
    normalizeWidgetTheme,
    APIURL,
    appendFlatpickrBaseStyles,
    initFlatpickrInShadow,
    cancelSelectEvent,
    getCSSStyles,
    getWidgetDataURI,
    apiGet,
    toggleWidgetTheme,
    getEChartsTooltipTheme,
    getHeaderPinStateClass,
    getHeaderPinButtonHTML,
    setupHeaderPin,
    getNoDataHTML
} from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

type EconomicScale = 'percentage_change' | 'absolute_values' | 'index_values';

const INDICATOR_OPTIONS = ['GDP', 'CPI', 'Inflation'] as const;

const INDICATOR_COLORS: Record<string, string> = {
    GDP: '#10B981',
    CPI: '#7C3AED',
    Inflation: '#60A5FA'
};

const SCALE_OPTIONS: { value: EconomicScale; label: string }[] = [
    { value: 'absolute_values', label: 'Absolute Values' },
    { value: 'percentage_change', label: 'Percentage Change' },
    { value: 'index_values', label: 'Index Values' }
];

interface USEconomicIndicatorsConfig extends WidgetConfig {
    start_date?: string;
    end_date?: string;
    scale?: EconomicScale;
    indicator_1?: string;
    indicator_2?: string;
    indicator_3?: string;
}

interface EconomicDataPoint {
    date: string;
    [key: string]: string | number | null | undefined;
}

export class TraderverseUSEconomicIndicators {
    private container: HTMLElement;
    private config: USEconomicIndicatorsConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: EconomicDataPoint[] | null = null;
    private currentChart: any;
    private selectedIndicators: Set<string> = new Set(['GDP', 'CPI', 'Inflation']);
    private startDate: string = '';
    private endDate: string = '';
    private scale: EconomicScale = 'absolute_values';
    private indicator1: string = 'CPI';
    private indicator2: string = 'CPI';
    private indicator3: string = 'Inflation';
    private widgetResizeObserver: ResizeObserver | null = null;
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;

    constructor(config: USEconomicIndicatorsConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultDateRange(): { startDate: string; endDate: string } {
        const today = new Date();
        const endDate = today.toISOString().slice(0, 10);
        const start = new Date(today);
        start.setFullYear(today.getFullYear() - 1);
        const startDate = start.toISOString().slice(0, 10);
        return { startDate, endDate };
    }

    private getDefaultConfig(): Partial<USEconomicIndicatorsConfig> {
        const { startDate, endDate } = this.getDefaultDateRange();
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            start_date: startDate,
            end_date: endDate,
            scale: 'absolute_values',
            indicator_1: 'CPI',
            indicator_2: 'CPI',
            indicator_3: 'Inflation'
        };
    }

    private applyConfigFilters(): void {
        const { startDate, endDate } = this.getDefaultDateRange();
        this.startDate = this.config.start_date || startDate;
        this.endDate = this.config.end_date || endDate;
        this.scale = (this.config.scale as EconomicScale) || 'absolute_values';
        this.indicator1 = this.config.indicator_1 || 'CPI';
        this.indicator2 = this.config.indicator_2 || 'CPI';
        this.indicator3 = this.config.indicator_3 || 'Inflation';
        this.selectedIndicators = new Set(
            [this.indicator1, this.indicator2, this.indicator3].filter(Boolean)
        );
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        this.attachStyles();
        this.setupEventListeners();
        await this.fetchData();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private getActiveIndicators(): string[] {
        return [this.indicator1, this.indicator2, this.indicator3].filter(Boolean);
    }

    private renderIndicatorOptions(selected: string, allowEmpty: boolean): string {
        const options = INDICATOR_OPTIONS.map(ind => {
            const isSelected = ind === selected ? ' selected' : '';
            return `<option value="${ind}"${isSelected}>${ind}</option>`;
        }).join('');
        const emptyOption = allowEmpty
            ? `<option value=""${selected === '' ? ' selected' : ''}>None</option>`
            : '';
        return emptyOption + options;
    }

    private renderLegendHtml(): string {
        return this.getActiveIndicators().map((indicator, idx) => {
            const color = INDICATOR_COLORS[indicator] || '#60A5FA';
            const isActive = this.selectedIndicators.has(indicator) ? ' active' : '';
            return `<span class="toggle-series${isActive}" data-series-index="${idx}" data-indicator="${indicator}" data-tooltip="${indicator}" style="background-color:${color};" data-testing-id="legend-${indicator.toLowerCase()}"></span>`;
        }).join('');
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
  <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} US Economic Indicators</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button" aria-label="Toggle filters">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow" data-testing-id="options-row">
                <div class="item dateRange" data-testing-id="date-range">
                    <input type="text" class="date-input" data-testing-id="date-input" />
                    <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/> </svg> </i>
                </div>
                <span></span>
                <div class="item" data-testing-id="legends-container">
                    <div class="legendsCollection" data-testing-id="legends-collection">
                        ${this.renderLegendHtml()}
                    </div>
                </div>
                <div class="filtersBox sideBar hidden">
                  <div class="item">
                    <label>Scale</label>
                    <select class="filter-scale" data-testing-id="filter-scale">
                      ${SCALE_OPTIONS.map(opt => `<option value="${opt.value}"${this.scale === opt.value ? ' selected' : ''}>${opt.label}</option>`).join('')}
                    </select>
                  </div>
                  <div class="item">
                    <label>Indicator 1</label>
                    <select class="filter-indicator-1" data-testing-id="filter-indicator-1">
                      ${this.renderIndicatorOptions(this.indicator1, false)}
                    </select>
                  </div>
                  <div class="item">
                    <label>Indicator 2</label>
                    <select class="filter-indicator-2" data-testing-id="filter-indicator-2">
                      ${this.renderIndicatorOptions(this.indicator2, true)}
                    </select>
                  </div>
                  <div class="item">
                    <label>Indicator 3</label>
                    <select class="filter-indicator-3" data-testing-id="filter-indicator-3">
                      ${this.renderIndicatorOptions(this.indicator3, true)}
                    </select>
                  </div>
                </div>
            </div>
            <div class="chartDv" data-testing-id="chart-container">
                <div class="chart" id="usEconomicIndicatorsChart" data-testing-id="economic-indicators-chart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);
        this.initDatePicker();
    }

    private initDatePicker(): void {
        const dateInput = this.shadowRoot.querySelector('.date-input') as HTMLInputElement | null;
        if (!dateInput) return;

        dateInput.value = `${this.startDate} to ${this.endDate}`;
        initFlatpickrInShadow(dateInput, this.shadowRoot, {
            mode: 'range',
            dateFormat: 'Y-m-d',
            defaultDate: [this.startDate, this.endDate],
            onClose: (selectedDates: Date[]) => {
                if (selectedDates.length === 2) {
                    const [start, end] = selectedDates;
                    this.startDate = start.toISOString().slice(0, 10);
                    this.endDate = end.toISOString().slice(0, 10);
                    this.config.start_date = this.startDate;
                    this.config.end_date = this.endDate;
                    dateInput.value = `${this.startDate} to ${this.endDate}`;
                    this.fetchData();
                }
            }
        });
    }

    private refreshLegends(): void {
        const legendsCollection = this.shadowRoot.querySelector('.legendsCollection') as HTMLElement | null;
        if (!legendsCollection) return;
        legendsCollection.innerHTML = this.renderLegendHtml();
        this.bindLegendToggles();
    }

    private bindLegendToggles(): void {
        const legendSpans = Array.from(this.shadowRoot.querySelectorAll('.toggle-series')) as HTMLElement[];
        legendSpans.forEach(span => {
            span.addEventListener('click', (e) => {
                const target = e.currentTarget as HTMLElement;
                const indicator = target.getAttribute('data-indicator') || '';
                if (!indicator) return;
                const isActiveNow = target.classList.toggle('active');

                if (isActiveNow) {
                    this.selectedIndicators.add(indicator);
                } else {
                    if (this.selectedIndicators.size <= 1) {
                        target.classList.add('active');
                        return;
                    }
                    this.selectedIndicators.delete(indicator);
                }

                this.updateDisplay();
            });
        });
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private setupEventListeners(): void {
        this.bindLegendToggles();

        const filtersBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLElement | null;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        if (filtersBtn && filtersBox) {
            filtersBtn.addEventListener('click', (ev: MouseEvent) => {
                ev.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });

            filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());

            this.outsideClickHandler = (ev: MouseEvent) => {
                const anyEv: any = ev;
                const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
                const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
                if (!clickedInside && !filtersBox.classList.contains('hidden')) {
                    filtersBox.classList.add('hidden');
                }
            };
            document.addEventListener('click', this.outsideClickHandler);
        }

        const scaleEl = this.shadowRoot.querySelector('.filter-scale') as HTMLSelectElement | null;
        const indicator1El = this.shadowRoot.querySelector('.filter-indicator-1') as HTMLSelectElement | null;
        const indicator2El = this.shadowRoot.querySelector('.filter-indicator-2') as HTMLSelectElement | null;
        const indicator3El = this.shadowRoot.querySelector('.filter-indicator-3') as HTMLSelectElement | null;

        const applyFiltersAndFetch = () => {
            this.scale = (scaleEl?.value as EconomicScale) || 'absolute_values';
            this.indicator1 = indicator1El?.value || 'CPI';
            this.indicator2 = indicator2El?.value || '';
            this.indicator3 = indicator3El?.value || '';

            this.config.scale = this.scale;
            this.config.indicator_1 = this.indicator1;
            this.config.indicator_2 = this.indicator2 || undefined;
            this.config.indicator_3 = this.indicator3 || undefined;

            const active = this.getActiveIndicators();
            this.selectedIndicators = new Set(active);

            this.refreshLegends();
            this.fetchData();
        };

        [scaleEl, indicator1El, indicator2El, indicator3El].forEach(el => {
            el?.addEventListener('change', applyFiltersAndFetch);
        });
    }

    private buildQueryParams(): Record<string, string> {
        const query: Record<string, string> = {
            start_date: this.startDate,
            end_date: this.endDate,
            scale: this.scale,
            indicator_1: this.indicator1
        };
        if (this.indicator2) query.indicator_2 = this.indicator2;
        if (this.indicator3) query.indicator_3 = this.indicator3;
        return query;
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async fetchData(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');

        try {
            const response = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/equity-market/us-economics-indicators`,
                query: this.buildQueryParams(),
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Failed to fetch data');

            const json = await response.json();
            if (json?.isSuccess && json.data && !json.data.error) {
                const rows = Array.isArray(json.data.data) ? json.data.data : [];
                this.currentData = rows.sort(
                    (a: EconomicDataPoint, b: EconomicDataPoint) =>
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                );
                this.emit('dataUpdate', json.data);
            } else {
                this.currentData = [];
                if (json?.data?.error) console.warn('US Economic Indicators:', json.data.error);
            }

            this.updateDisplay();
        } catch (err) {
            console.error('Error fetching chart data:', err);
            this.currentData = [];
            this.updateDisplay();
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData || !(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private formatDateLabel(dateString: string): string {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    private formatValue(value: number | null | undefined): string {
        if (value === null || value === undefined) return '-';
        if (this.scale === 'percentage_change') return `${value}%`;
        return String(value);
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#usEconomicIndicatorsChart') as HTMLElement;
        const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;

        if (!container) return;

        const hasData = Array.isArray(this.currentData) && this.currentData.length > 0;
        if (!hasData) {
            chartDv?.removeAttribute('data-testing-id');
            container.innerHTML = getNoDataHTML();
            if (this.currentChart) {
                try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
                this.currentChart = null;
            }
            return;
        }

        chartDv?.setAttribute('data-testing-id', 'us-economic-indicators-chartdv');

        const arr = this.currentData!;
        const dates = arr.map(item => this.formatDateLabel(item.date));
        const selected = Array.from(this.selectedIndicators);
        const series: any[] = [];

        for (const indicator of selected) {
            if (!this.getActiveIndicators().includes(indicator)) continue;
            const seriesData = arr.map(item => {
                const value = item[indicator];
                return value === undefined || value === null ? null : Number(value);
            });
            series.push({
                name: indicator,
                type: 'line',
                smooth: true,
                data: seriesData,
                showSymbol: false,
                lineStyle: { width: 2 },
                itemStyle: {}
            });
        }

        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
            this.currentChart = null;
        }

        const chart = echarts.init(container);
        this.currentChart = chart;

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
        const palette = selected.map(ind => INDICATOR_COLORS[ind] || '#60A5FA');

        const option: any = {
            backgroundColor: 'transparent',
            color: palette,
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'line' },
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    if (!params || params.length === 0) return '';
                    const axisName = params[0].axisValueLabel || '';
                    const lines = params.map(
                        (p: any) => `${p.marker} ${p.seriesName}: ${this.formatValue(p.data)}`
                    );
                    return `<div>${axisName}</div><div>${lines.join('<br/>')}</div>`;
                }
            },
            legend: { show: false },
            grid: {
                top: '10',
                left: '0',
                right: '20',
                bottom: '0',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: dates,
                boundaryGap: false,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { color: textColor, fontSize: 12 }
            },
            yAxis: {
                type: 'value',
                scale: true,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: textColor,
                    formatter: (v: any) => {
                        if (v === null || v === undefined) return '';
                        return this.scale === 'percentage_change' ? `${v}%` : String(v);
                    }
                },
                splitLine: { show: true, lineStyle: { color: splitLineColor } }
            },
            series: series.map((s: any, i: number) => {
                const indicatorName = s?.name ? String(s.name) : String(i);
                const color = INDICATOR_COLORS[indicatorName] || palette[i % palette.length];
                return {
                    ...s,
                    lineStyle: { width: 2, color },
                    itemStyle: { color },
                    showSymbol: false
                };
            })
        };

        chart.setOption(option);

        const legendSpans = Array.from(this.shadowRoot.querySelectorAll('.toggle-series')) as HTMLElement[];
        legendSpans.forEach(span => {
            const indicator = span.getAttribute('data-indicator') || '';
            span.classList.toggle('active', this.selectedIndicators.has(indicator));
        });
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as
            | 'white'
            | 'black'
            | 'default-dark'
            | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global {
    interface Window {
        TraderverseUSEconomicIndicators: typeof TraderverseUSEconomicIndicators;
    }
}
if (typeof window !== 'undefined') {
    window.TraderverseUSEconomicIndicators = TraderverseUSEconomicIndicators;
}
